import useUser from "@/api/apis";
import { useCurrency } from "@/hooks/useCurrency";
import { GameResell, GameResellKeyCount, Platform } from "@/models/GameResell";
import { AuthUtils } from "@/utils/AuthUtils";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/v2-components/ui/radio-group";
import { Button } from "@/v2-components/ui/button";
import { Badge } from "@/v2-components/ui/badge";
import { createContext, useContext } from "react";
import { CurrencyIsoCode } from "@/enums";
import { cn } from "@/libs/utils";
import { useAddGameItem } from "@/store/cart-store";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import MetaColoredIcon from "@/v2-components/core/icon/meta-colored-icon";
import SteamColoredIcon from "@/v2-components/core/icon/steam-colored-icon";
import { GePrice } from "@/v2-components/GePrice";
import { useTrans } from "@/utils/useTrans";
import { useLocale } from "@/store/locale-store";
import { GameUtil } from "@/utils/GameUtil";
import { sellableKeyPlatforms } from "@/libs/cart/cart-game-stock";

interface GameKeyPlatform {
  platform: Platform;
  discountPercentile: number;
  price: number;
  discount: number;
  currency: CurrencyIsoCode;
}

/**
 * 팔 플랫폼이 없을 때 그 이유 — 버튼 문구만 가른다(막는 건 `platformList` 가 비었다는 사실 하나다).
 * `checking` = 재고를 아직 모른다(조회 중 · 서버가 확인 못 함 · 조회 실패 — 훅이 다시 묻는다). 문구는 장바구니의
 * 「재고 확인 중」과 같은 키다(`docs/cart-game-key-stock-policy.md`).
 */
type StockStatus = "available" | "soldout" | "checking";

interface GameKeySellerContext {
  gameResellId: string;
  /** 지금 팔 수 있는 플랫폼만 — `sellableKeyPlatforms`. 재고를 모르면 비어 있다. */
  platformList: GameKeyPlatform[];
  stockStatus: StockStatus;
  disableCountryList: string[];
  /** 항상 `platformList` 안의 값이거나(비어 있으면) `undefined` 다 — Root 가 맞춘다. */
  selectedPlatform: string | undefined;
  updateSelectedPlatform: (platform: string) => void;
  /**
   * `add_to_cart` 의 진입점 (#5292).
   *
   * ⚠️ **이 컴파운드를 두 표면이 그린다** — 게임 목록·카드의 구매 다이얼로그와 게임 상세
   * 페이지의 구매 히어로다. 그래서 값을 여기서 정하면 반드시 한쪽이 틀린다. 필수 prop 으로
   * 두어 **표면이 정하게** 한다.
   */
  trigger: CartTrigger;
}

const GameKeySellerContext = createContext<GameKeySellerContext | null>(null);

const GameKeySellerProvider = GameKeySellerContext.Provider;

const useGameKeySellerContext = () => {
  const context = useContext(GameKeySellerContext);
  if (!context) {
    throw new Error(
      "GameKeySeller compound components cannot be rendered outside the GameKeySeller component",
    );
  }
  return context;
};

export { GameKeySellerProvider, useGameKeySellerContext };

function GameKeySellerRoot({
  gameResell,
  keyCounts,
  children,
  className,
  trigger,
}: {
  gameResell: GameResell;
  /**
   * 플랫폼별 키 재고 — 배열(확인한 재고) · `null`(서버가 확인 못 함) · `undefined`(조회 중·실패).
   * ⚠️ 호출처에서 `?? []` 로 접지 말 것 — 팔지 않는 건 같지만 「확인 중」이 「품절」 안내로 바뀐다.
   */
  keyCounts: GameResellKeyCount[] | null | undefined;
  children: React.ReactNode;
  className?: string;
  /** `add_to_cart` 진입점 — 기본값을 두지 않는다(#5292). 표면이 정한다. */
  trigger: CartTrigger;
}) {
  const currency = useCurrency();

  const [selectedPlatform, setSelectedPlatform] = useState<string>();

  // 목록 「재고 있음」 · 상세 히어로 게이트와 같은 판정(`sellableKeyPlatforms` — 술어는 장바구니와 같은
  // `platformKeyStock`)이라 재고를 모르면 비운다. 종전엔 여기서만 「재고 행이 없으면 판다」(`find(…)?.pending === 0` 이
  // 거짓)로 읽어, 조회 실패 · 로딩 중에도 가격 있는 플랫폼을 전부 담을 수 있었다(#5942 리뷰).
  const platformList = sellableKeyPlatforms(
    gameResell.platformList,
    keyCounts,
    currency,
  ).map((item) => {
    const price = GameUtil.findPrice(item.priceList, currency);

    return {
      platform: item.platform,
      discountPercentile: price?.discountPercentile ?? 0,
      price: price?.price ?? 100000,
      discount: price?.discount ?? 0,
      currency: currency,
    };
  });

  const stockStatus: StockStatus =
    platformList.length > 0
      ? "available"
      : Array.isArray(keyCounts)
        ? "soldout"
        : "checking";

  // 고른 플랫폼은 **지금 목록에서** 푼다. 종전엔 라디오 그룹이 마운트 때 한 번 첫 플랫폼을 골랐다 — 다이얼로그는
  // 재고가 오기 전에 마운트되므로, 도착한 재고가 그 플랫폼을 품절로 지워도 선택이 남아 품절 키가 담겼다.
  // 목록에 없는 선택(늦게 온 재고 · 다시 물어 품절)은 첫 플랫폼으로 돌아간다.
  const effectiveSelectedPlatform = platformList.some(
    (item) => item.platform === selectedPlatform,
  )
    ? selectedPlatform
    : platformList[0]?.platform;

  return (
    <GameKeySellerProvider
      value={{
        gameResellId: gameResell.id,
        platformList,
        stockStatus,
        selectedPlatform: effectiveSelectedPlatform,
        disableCountryList: gameResell.disableCountryList,
        updateSelectedPlatform: setSelectedPlatform,
        trigger,
      }}
    >
      <div className={cn("flex flex-col gap-5 laptop:gap-6", className)}>
        {children}
      </div>
    </GameKeySellerProvider>
  );
}

interface GameKeySellerRadioGroupProps {
  variant?: "default" | "blur";
  showRadioIcon?: boolean;
  className?: string;
}
function GameKeySellerRadioGroup({
  variant = "default",
  showRadioIcon = false,
  className,
}: GameKeySellerRadioGroupProps) {
  const { user } = useUser();
  const { t } = useTrans(["gnb"]);
  const { countryCode } = useLocale();

  const { updateSelectedPlatform, platformList, selectedPlatform } =
    useGameKeySellerContext();

  const platformMap = {
    [Platform.Quest]: {
      icon: <MetaColoredIcon />,
      label: "Meta",
    },
    [Platform.Steam]: {
      icon: <SteamColoredIcon />,
      label: "Steam",
    },
  };

  // 기본 선택(첫 플랫폼)은 Root 가 목록에서 푼다 — 마운트 때 한 번 고르면 재고가 늦게 올 때 어긋난다.
  // 고를 플랫폼이 없을 땐(재고 확인 중 · 품절) `""` 로 둔다 — `undefined` 면 재고가 온 순간 Radix 가 uncontrolled →
  // controlled 로 바뀌었다고 경고한다(다이얼로그는 재고 전에 열린다).
  return (
    <RadioGroup
      value={selectedPlatform ?? ""}
      className={cn("flex flex-col gap-3", className)}
    >
      {platformList.map((item) => {
        const { discountPercentile, platform } = item;
        const discountApplied = t("{{percent}}% Off Applied", {
          percent: discountPercentile,
          defaultValue: `${discountPercentile}% Off Applied`,
        });
        const discountCoupon = t("{{percent}}% Off Coupon", {
          percent: discountPercentile,
          defaultValue: `${discountPercentile}% Off Coupon`,
        });
        const couponText = !!user ? discountApplied : discountCoupon;
        const platformText = t("{{platform}} Game Key", {
          platform: platformMap[platform].label,
          defaultValue: `${platformMap[platform].label} Game Key`,
        });

        const badgeVariant =
          variant === "blur"
            ? user
              ? "new-text"
              : "sale-text"
            : user
              ? "new"
              : "sale";

        return (
          <div key={item.platform}>
            <label
              htmlFor={item.platform}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg transition-colors hover:border-coolgray-90",
                /**
                 * ★패딩이 variant 마다 다르다 — 시안 `29889:101388` 의 `blur` 행은 **8** 이다
                 * (실측 16 이었다). `default` 는 보더가 있는 카드라 16 을 유지한다.
                 *
                 * ⚠️ 공통으로 `p-2` 를 주면 `SelectGameKeyDialog` 와 웹뷰까지 바뀐다 —
                 * `variant="blur"` 소비처는 게임 상세 히어로 **한 곳**뿐이라 거기에만 건다.
                 */
                variant === "blur" ? "p-2" : "p-4",
                variant === "default" && "border border-coolgray-20",
                selectedPlatform === item.platform &&
                  (variant === "default"
                    ? "outline outline-2 -outline-offset-2 outline-blue-60"
                    : "bg-white/[12%]"),
              )}
              onClick={() => updateSelectedPlatform(item.platform)}
            >
              <RadioGroupItem
                id={item.platform}
                value={item.platform}
                className={cn("peer text-white", !showRadioIcon && "sr-only")}
              />
              <div className="flex w-full flex-col gap-2">
                <p className="flex items-center gap-2 text-base">
                  {platformMap[item.platform].icon}
                  <span>{platformText}</span>
                </p>
                <div className="flex flex-wrap items-center justify-between gap-2 text-lg laptop:text-3xl">
                  <div className="flex items-center gap-2">
                    <div className="font-500">
                      <GePrice
                        amount={item.price - item.discount}
                        currency={item.currency}
                        countryCode={countryCode}
                      />
                    </div>
                    {/**
                     * 정가(줄긋기) — **variant 마다 배경이 달라 색이 다르다.**
                     *
                     * `default` 는 흰 배경 카드라 `coolgray-50` 이 맞고, `blur` 는 어두운 히어로
                     * 이미지 위 반투명 패널이라 시안이 `line-through opacity-30`(흰 글자 상속)을
                     * 쓴다(`29889:101397`). 회색을 그대로 두면 어두운 배경에서 묻힌다.
                     */}
                    <div
                      className={cn(
                        "line-through",
                        variant === "blur" ? "opacity-30" : "text-coolgray-50",
                      )}
                    >
                      <GePrice
                        amount={item.price}
                        currency={item.currency}
                        countryCode={countryCode}
                        hideCurrency
                      />
                    </div>
                  </div>
                  <Badge size="sm" variant={badgeVariant}>
                    {couponText}
                  </Badge>
                </div>
              </div>
            </label>
          </div>
        );
      })}
    </RadioGroup>
  );
}

function GameKeySellerButton({
  className,
  onClickEnd,
}: {
  className?: string;
  onClickEnd?: () => void;
}) {
  const { t } = useTrans(["gnb"]);
  const router = useRouter();
  const { user } = useUser();
  const {
    platformList,
    stockStatus,
    disableCountryList,
    selectedPlatform,
    gameResellId,
    trigger,
  } = useGameKeySellerContext();
  const addGameItem = useAddGameItem();

  // 팔 플랫폼이 없으면(재고 확인 중 · 모름 · 품절) 담기와 로그인 유도를 둘 다 막는다 — Root 가 선택을 목록 안에서
  // 풀므로, 선택이 없다는 건 목록이 비었다는 뜻이다.
  const purchasable = !!selectedPlatform;

  const handleButtonClick = () => {
    if (!selectedPlatform) {
      return;
    }

    if (user) {
      addGameItem(
        {
          platform: selectedPlatform,
          gameResellId,
          disableCountryList,
        },
        trigger,
      );
    } else {
      router.push(AuthUtils.getLoginUrl());
    }
    onClickEnd?.();
  };

  const maxDiscountPercentile = Math.max(
    ...platformList.map((item) => item.discountPercentile),
  );

  // 목록이 비면 `maxDiscountPercentile` 은 `-Infinity` 다 — 쿠폰 문구를 만들지 않는다.
  const buttonText = !purchasable
    ? stockStatus === "soldout"
      ? t("Sold Out")
      : t("Checking stock")
    : user
      ? t("Add to Bag")
      : t(`Log in to Get {{percent}}% Off Coupon`, {
          ns: "gnb",
          percent: maxDiscountPercentile,
          defaultValue: `Log in to Get ${maxDiscountPercentile}% Off Coupon`,
        });

  return (
    <Button
      variant="blue-fill"
      shape="pill"
      onClick={handleButtonClick}
      disabled={!purchasable}
      size="none"
      className={cn("h-10 text-sm laptop:h-[54px] laptop:text-lg", className)}
    >
      {buttonText}
    </Button>
  );
}

const GameKeySeller = Object.assign(GameKeySellerRoot, {
  RadioGroup: GameKeySellerRadioGroup,
  Button: GameKeySellerButton,
});

export default GameKeySeller;
